import React, { Suspense, useEffect, useRef } from 'react';
import { createDrawerNavigator } from '@react-navigation/drawer';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { ActivityIndicator, I18nManager, Platform, useWindowDimensions, View, Text } from 'react-native';
import { CommonActions, useNavigation } from '@react-navigation/native';
import { COLORS } from '../constants/config';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { isEmployee, isOrgExecutive, isDG, canCreateStrategicGoals, isEvaluator } from '../utils/userPermissions';
import { navigationRef } from '../services/navigationService';

// =====================================================================
// Lazy loading helper for non-critical screens.
// Each wrapped screen gets its own Suspense boundary so React Navigation
// never throws while the chunk is still being fetched. Only use for routes
// that are NOT on the dashboard critical path.
// =====================================================================
function ScreenFallback() {
  return (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffffff' }}>
      <ActivityIndicator size="large" color={COLORS.primary} />
    </View>
  );
}

function lazyScreen<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
): React.ComponentType<any> {
  const LazyComponent = React.lazy(importFn);
  const Wrapped: React.FC<any> = (props) => (
    <Suspense fallback={<ScreenFallback />}>
      <LazyComponent {...props} />
    </Suspense>
  );
  return Wrapped;
}

// =====================================================================
// Eager imports — critical path (dashboard + core CRUD)
// =====================================================================
import DashboardScreen from '../screens/dashboard/DashboardScreen';
import MyOrganizationScreen from '../screens/myorganization/MyOrganizationScreen';
import EditOrganizationScreen from '../screens/myorganization/EditOrganizationScreen';
import AddOrganizationalUnitScreen from '../screens/myorganization/AddOrganizationalUnitScreen';
import OrganizationalUnitDetailScreen from '../screens/myorganization/OrganizationalUnitDetailScreen';
import AddEmployeeScreen from '../screens/myorganization/AddEmployeeScreen';
import EmployeeDetailScreen from '../screens/myorganization/EmployeeDetailScreen';
import OrganizationsScreen from '../screens/organizations/OrganizationsScreen';
import OrganizationDetailScreen from '../screens/organizations/OrganizationDetailScreen';
import GoalsScreen from '../screens/goals/GoalsScreen';
import GoalDetailScreen from '../screens/goals/GoalDetailScreen';
import AddGoalScreen from '../screens/goals/AddGoalScreen';
import EmiratePlanScreen from '../screens/emirateplan/EmiratePlanScreen';
import PartnersScreen from '../screens/partners/PartnersScreen';
import CalibrationMeetingsScreen from '../screens/calibration/CalibrationMeetingsScreen';
import AuditLogScreen from '../screens/audit/AuditLogScreen';
import RecycleBinScreen from '../screens/recyclebin/RecycleBinScreen';
import InitiativesScreen from '../screens/initiatives/InitiativesScreen';
import InitiativeDetailScreen from '../screens/initiatives/InitiativeDetailScreen';
import AddInitiativeScreen from '../screens/initiatives/AddInitiativeScreen';
import AddActivityScreen from '../screens/initiatives/AddActivityScreen';
import ActivityDetailScreen from '../screens/initiatives/ActivityDetailScreen';
import UnitGoalsScreen from '../screens/unitgoals/UnitGoalsScreen';
import UnitGoalDetailScreen from '../screens/unitgoals/UnitGoalDetailScreen';
import AddUnitGoalScreen from '../screens/unitgoals/AddUnitGoalScreen';
import ProcessesScreen from '../screens/processes/ProcessesScreen';
import ProcessDetailScreen from '../screens/processes/ProcessDetailScreen';
import SubProcessDetailScreen from '../screens/processes/SubProcessDetailScreen';
import AddProcessScreen from '../screens/processes/AddProcessScreen';
import AddSubProcessScreen from '../screens/processes/AddSubProcessScreen';
import AddOperationScreen from '../screens/processes/AddOperationScreen';
import ServicesScreen from '../screens/services/ServicesScreen';
import RisksScreen from '../screens/risks/RisksScreen';
import AddRiskScreen from '../screens/risks/AddRiskScreen';
import EmployeesScreen from '../screens/employees/EmployeesScreen';
import ExportScreen from '../screens/export/ExportScreen';
import KPIsScreen from '../screens/kpis/KPIsScreen';
import KPIDetailScreen from '../screens/kpis/KPIDetailScreen';
import AddInitiativeKPIScreen from '../screens/kpis/AddInitiativeKPIScreen';
import AddKPIReadingScreen from '../screens/kpis/AddKPIReadingScreen';
import AddServiceScreen from '../screens/services/AddServiceScreen';
import ServiceDetailScreen from '../screens/services/ServiceDetailScreen';
import SchedulesScreen from '../screens/schedules/SchedulesScreen';
import SchedulesManagementScreen from '../screens/schedules/SchedulesManagementScreen';
import NotificationsScreen from '../screens/notifications/NotificationsScreen';
import ProfileScreen from '../screens/profile/ProfileScreen';
import SettingsScreen from '../screens/settings/SettingsScreen';
import SearchResultsScreen from '../screens/search/SearchResultsScreen';
import CustomDrawerContent from '../components/CustomDrawerContent';

// Strategy Team Screens
import {
  StrategyDashboardScreen,
  OrganizationsListScreen,
  StrategyOrganizationsScreen,
  PendingApprovalsScreen,
  PerformanceDeviationScreen,
} from '../screens/strategy';

// =====================================================================
// Lazy imports — non-critical routes (evaluation, imports, admin, help).
// These are split out of the initial JS bundle and fetched on first
// navigation. Shaves ~150–250KB gzipped off cold load.
// =====================================================================
const ImportEmployeesScreen = lazyScreen(() => import('../screens/employees/ImportEmployeesScreen'));
const ImportUnitGoalsScreen = lazyScreen(() => import('../screens/unitgoals/ImportUnitGoalsScreen'));
const ImportProcessesScreen = lazyScreen(() => import('../screens/processes/ImportProcessesScreen'));
const ImportPartnersScreen = lazyScreen(() => import('../screens/partners/ImportPartnersScreen'));
const DocumentsScreen = lazyScreen(() => import('../screens/documents/DocumentsScreen'));
const AddDocumentScreen = lazyScreen(() => import('../screens/documents/AddDocumentScreen'));
// AdminScreen removed 2026-05-13 — user management lives in DTS-SSO.
const HelpScreen = lazyScreen(() => import('../screens/help/HelpScreen'));
const EvaluationDashboard = lazyScreen(() => import('../screens/evaluation/EvaluationDashboard'));
const EvaluationDetail = lazyScreen(() => import('../screens/evaluation/EvaluationDetail'));
const CriteriaScoringForm = lazyScreen(() => import('../screens/evaluation/CriteriaScoringForm'));
const EvaluationReadOnly = lazyScreen(() => import('../screens/evaluation/EvaluationReadOnly'));
const AnnualEvalTable = lazyScreen(() => import('../screens/evaluation/AnnualEvalTable'));
const MonthlyEvalTable = lazyScreen(() => import('../screens/evaluation/MonthlyEvalTable'));
const EvaluationManagement = lazyScreen(() => import('../screens/evaluation/EvaluationManagement'));
const FrameworkBuilder = lazyScreen(() => import('../screens/evaluation/FrameworkBuilder'));


// Enable RTL
I18nManager.forceRTL(true);
I18nManager.allowRTL(true);

// Types
export type MainStackParamList = {
  Dashboard: { organizationId?: number } | undefined;
  MyOrganization: { scrollToUnits?: boolean } | undefined;
  EditOrganization: { organizationId: number };
  AddOrganizationalUnit: { unitId?: number; parentId?: number; parentName?: string; scrollToKpi?: boolean };
  OrganizationalUnitDetail: { id: number };
  AddEmployee: { employeeId?: number; unitId?: number; source?: 'myorganization' | 'employees' };
  EmployeeDetail: { id: number; source?: 'myorganization' | 'employees' };
  Organizations: undefined;
  OrganizationDetail: { id: number };
  Goals: { organizationId?: number };
  GoalDetail: { id: number; fromProcess?: { id: number; name: string } };
  AddGoal: { goalId?: number; organizationId?: number; unitId?: number; scrollToKpi?: boolean };
  EmiratePlan: undefined;
  Partners: undefined;
  CalibrationMeetings: undefined;
  AuditLog: undefined;
  RecycleBin: undefined;
  Initiatives: { goalId?: number; organizationId?: number };
  InitiativeDetail: { id: number };
  AddInitiative: { goalId?: number; initiativeId?: number; scrollToKpi?: boolean };
  AddService: { goalId?: number; serviceId?: number; scrollToKpi?: boolean };
  ServiceDetail: { id: number };
  UnitGoals: { organizationId?: number; organizationName?: string } | undefined;
  UnitGoalDetail: { id: number };
  AddUnitGoal: { unitGoalId?: number; strategicGoalId?: number; unitId?: number; scrollToKpi?: boolean };
  Processes: { organizationId?: number; organizationName?: string } | undefined;
  ProcessDetail: { id: number };
  SubProcessDetail: { id: number };
  AddProcess: { processId?: number; scrollToKpi?: boolean; goalId?: number; organizationalUnitId?: number };
  AddSubProcess: { processId?: number; subProcessId?: number; scrollToKpi?: boolean };
  AddOperation: { processId?: number; subProcessId?: number; operationId?: number };
  Services: { organizationId?: number; organizationName?: string } | undefined;
  Risks: { successMessage?: string } | undefined;
  AddRisk: { riskId?: number; relatedType?: string; relatedId?: number };
  Employees: { organizationId?: number; organizationName?: string } | undefined;
  ImportEmployees: undefined;
  ImportUnitGoals: undefined;
  ImportProcesses: undefined;
  ImportPartners: undefined;
  Documents: undefined;
  AddDocument: { documentId?: number } | undefined;
  Export: undefined;
  Admin: undefined;
  Help: undefined;
  KPIs: { organizationId?: number; goalId?: number; initiativeId?: number };
  KPIDetail: { id: number };
  AddKPIReading: { kpiId: number; readingId?: number };
  AddInitiativeKPI: { initiativeId: number };
  AddActivity: { initiativeId: number; activityId?: number; scrollToKpi?: boolean };
  ActivityDetail: { id: number };
  Schedules: { organizationId?: number; filter?: 'all' | 'pending' | 'overdue' | 'completed' };
  SchedulesManagement: undefined;
  Notifications: undefined;
  Profile: undefined;
  Settings: undefined;
  SearchResults: { query?: string };
  // Strategy Team Screens
  StrategyDashboard: undefined;
  StrategyOrganizations: undefined;
  OrganizationsList: undefined;
  PendingApprovals: undefined;
  PerformanceDeviation: undefined;
  // Evaluation Screens
  EvaluationDashboard: undefined;
  EvaluationDetail: { assignmentId: number };
  CriteriaScoringForm: { assignmentId: number; axisId: number; criteriaIndex?: number };
  EvaluationReadOnly: { evaluationId: number; axisId?: number; assignmentId?: number };
  AnnualEvalTable: { departmentId: number };
  MonthlyEvalTable: { departmentId: number };
  EvaluationManagement: undefined;
  FrameworkBuilder: { frameworkId?: number };
};

const Drawer = createDrawerNavigator();
const Stack = createNativeStackNavigator<MainStackParamList>();

// Screen options - hide headers (each screen has its own header design)
const stackScreenOptions = {
  headerShown: false,
};

// Stack Navigator for each section
function DashboardStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Dashboard" component={DashboardScreen} />
      <Stack.Screen name="EditOrganization" component={EditOrganizationScreen} />
      {/* Lists reachable from the dashboard's "View Details" cards. Keeping
          them inside the same stack means browser/native Back naturally pops
          to /dashboard/:id (or /dashboard) instead of jumping across stacks
          via the drawer and losing the org-inspection context. */}
      <Stack.Screen name="Employees" component={EmployeesScreen} />
      <Stack.Screen name="EmployeeDetail" component={EmployeeDetailScreen} />
      <Stack.Screen name="AddEmployee" component={AddEmployeeScreen} />
      <Stack.Screen name="UnitGoals" component={UnitGoalsScreen} />
      <Stack.Screen name="UnitGoalDetail" component={UnitGoalDetailScreen} />
      <Stack.Screen name="AddUnitGoal" component={AddUnitGoalScreen} />
      <Stack.Screen name="Services" component={ServicesScreen} />
      <Stack.Screen name="ServiceDetail" component={ServiceDetailScreen} />
      <Stack.Screen name="AddService" component={AddServiceScreen} />
      <Stack.Screen name="Processes" component={ProcessesScreen} />
      <Stack.Screen name="ProcessDetail" component={ProcessDetailScreen} />
      <Stack.Screen name="AddProcess" component={AddProcessScreen} />
      <Stack.Screen name="SubProcessDetail" component={SubProcessDetailScreen} />
      <Stack.Screen name="AddSubProcess" component={AddSubProcessScreen} />
      <Stack.Screen name="AddOperation" component={AddOperationScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      {/* Top-level stat cards (الأهداف / المبادرات / المؤشرات) also need to
          live inside DashboardStack so back-navigation from those lists pops
          to /dashboard/:id instead of jumping stacks. */}
      <Stack.Screen name="Goals" component={GoalsScreen} />
      <Stack.Screen name="GoalDetail" component={GoalDetailScreen} />
      <Stack.Screen name="AddGoal" component={AddGoalScreen} />
      <Stack.Screen name="EmiratePlan" component={EmiratePlanScreen} />
      <Stack.Screen name="Partners" component={PartnersScreen} />
      <Stack.Screen name="ImportPartners" component={ImportPartnersScreen} />
      <Stack.Screen name="Documents" component={DocumentsScreen} />
      <Stack.Screen name="AddDocument" component={AddDocumentScreen} />
      <Stack.Screen name="CalibrationMeetings" component={CalibrationMeetingsScreen} />
      <Stack.Screen name="AuditLog" component={AuditLogScreen} />
      <Stack.Screen name="RecycleBin" component={RecycleBinScreen} />
      <Stack.Screen name="Initiatives" component={InitiativesScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="AddInitiative" component={AddInitiativeScreen} />
      <Stack.Screen name="ActivityDetail" component={ActivityDetailScreen} />
      <Stack.Screen name="AddActivity" component={AddActivityScreen} />
      <Stack.Screen name="KPIs" component={KPIsScreen} />
      <Stack.Screen name="AddInitiativeKPI" component={AddInitiativeKPIScreen} />
    </Stack.Navigator>
  );
}

function MyOrganizationStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="MyOrganization" component={MyOrganizationScreen} />
      <Stack.Screen name="EditOrganization" component={EditOrganizationScreen} />
      <Stack.Screen name="AddOrganizationalUnit" component={AddOrganizationalUnitScreen} />
      <Stack.Screen name="OrganizationalUnitDetail" component={OrganizationalUnitDetailScreen} />
      <Stack.Screen name="AddEmployee" component={AddEmployeeScreen} />
      <Stack.Screen name="EmployeeDetail" component={EmployeeDetailScreen} />
      <Stack.Screen name="AddGoal" component={AddGoalScreen} />
      <Stack.Screen name="GoalDetail" component={GoalDetailScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="AddInitiative" component={AddInitiativeScreen} />
      <Stack.Screen name="ActivityDetail" component={ActivityDetailScreen} />
      <Stack.Screen name="AddActivity" component={AddActivityScreen} />
      <Stack.Screen name="ServiceDetail" component={ServiceDetailScreen} />
      <Stack.Screen name="AddService" component={AddServiceScreen} />
      <Stack.Screen name="ProcessDetail" component={ProcessDetailScreen} />
      <Stack.Screen name="AddProcess" component={AddProcessScreen} />
      <Stack.Screen name="SubProcessDetail" component={SubProcessDetailScreen} />
      <Stack.Screen name="AddSubProcess" component={AddSubProcessScreen} />
      <Stack.Screen name="AddOperation" component={AddOperationScreen} />
      <Stack.Screen name="UnitGoalDetail" component={UnitGoalDetailScreen} />
      <Stack.Screen name="AddUnitGoal" component={AddUnitGoalScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
    </Stack.Navigator>
  );
}

function OrganizationsStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Organizations" component={OrganizationsScreen} />
      <Stack.Screen name="OrganizationDetail" component={OrganizationDetailScreen} />
      <Stack.Screen name="Employees" component={EmployeesScreen} />
      <Stack.Screen name="UnitGoals" component={UnitGoalsScreen} />
      <Stack.Screen name="Services" component={ServicesScreen} />
      <Stack.Screen name="Processes" component={ProcessesScreen} />
    </Stack.Navigator>
  );
}

function GoalsStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Goals" component={GoalsScreen} />
      <Stack.Screen name="GoalDetail" component={GoalDetailScreen} />
      <Stack.Screen name="AddGoal" component={AddGoalScreen} />
      <Stack.Screen name="EmiratePlan" component={EmiratePlanScreen} />
      <Stack.Screen name="Partners" component={PartnersScreen} />
      <Stack.Screen name="ImportPartners" component={ImportPartnersScreen} />
      <Stack.Screen name="Documents" component={DocumentsScreen} />
      <Stack.Screen name="AddDocument" component={AddDocumentScreen} />
      <Stack.Screen name="CalibrationMeetings" component={CalibrationMeetingsScreen} />
      <Stack.Screen name="AuditLog" component={AuditLogScreen} />
      <Stack.Screen name="RecycleBin" component={RecycleBinScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="AddInitiative" component={AddInitiativeScreen} />
      <Stack.Screen name="ActivityDetail" component={ActivityDetailScreen} />
      <Stack.Screen name="AddActivity" component={AddActivityScreen} />
      <Stack.Screen name="AddService" component={AddServiceScreen} />
      <Stack.Screen name="ServiceDetail" component={ServiceDetailScreen} />
      <Stack.Screen name="AddProcess" component={AddProcessScreen} />
      <Stack.Screen name="ProcessDetail" component={ProcessDetailScreen} />
      <Stack.Screen name="SubProcessDetail" component={SubProcessDetailScreen} />
      <Stack.Screen name="AddSubProcess" component={AddSubProcessScreen} />
      <Stack.Screen name="AddOperation" component={AddOperationScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      <Stack.Screen name="AddUnitGoal" component={AddUnitGoalScreen} />
      <Stack.Screen name="UnitGoalDetail" component={UnitGoalDetailScreen} />
    </Stack.Navigator>
  );
}

function InitiativesStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Initiatives" component={InitiativesScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="AddInitiative" component={AddInitiativeScreen} />
      <Stack.Screen name="AddInitiativeKPI" component={AddInitiativeKPIScreen} />
      <Stack.Screen name="AddActivity" component={AddActivityScreen} />
      <Stack.Screen name="ActivityDetail" component={ActivityDetailScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
    </Stack.Navigator>
  );
}

function UnitGoalsStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="UnitGoals" component={UnitGoalsScreen} />
      <Stack.Screen name="UnitGoalDetail" component={UnitGoalDetailScreen} />
      <Stack.Screen name="AddUnitGoal" component={AddUnitGoalScreen} />
      <Stack.Screen name="ImportUnitGoals" component={ImportUnitGoalsScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
    </Stack.Navigator>
  );
}

function ProcessesStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Processes" component={ProcessesScreen} />
      <Stack.Screen name="ProcessDetail" component={ProcessDetailScreen} />
      <Stack.Screen name="SubProcessDetail" component={SubProcessDetailScreen} />
      <Stack.Screen name="AddProcess" component={AddProcessScreen} />
      <Stack.Screen name="AddSubProcess" component={AddSubProcessScreen} />
      <Stack.Screen name="AddOperation" component={AddOperationScreen} />
      <Stack.Screen name="GoalDetail" component={GoalDetailScreen} />
      <Stack.Screen name="AddGoal" component={AddGoalScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="AddInitiative" component={AddInitiativeScreen} />
      <Stack.Screen name="ActivityDetail" component={ActivityDetailScreen} />
      <Stack.Screen name="AddActivity" component={AddActivityScreen} />
      <Stack.Screen name="ServiceDetail" component={ServiceDetailScreen} />
      <Stack.Screen name="AddService" component={AddServiceScreen} />
      <Stack.Screen name="UnitGoalDetail" component={UnitGoalDetailScreen} />
      <Stack.Screen name="AddUnitGoal" component={AddUnitGoalScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      <Stack.Screen name="ImportProcesses" component={ImportProcessesScreen} />
    </Stack.Navigator>
  );
}

function ServicesStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Services" component={ServicesScreen} />
      <Stack.Screen name="AddService" component={AddServiceScreen} />
      <Stack.Screen name="ServiceDetail" component={ServiceDetailScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
    </Stack.Navigator>
  );
}

// الدلائل والمرجعيات — authority-wide document library. Its own drawer-level
// stack so the sidebar item routes role-agnostically (org / strategy / DG /
// evaluator all share it). Previously Documents existed only nested inside
// other stacks, so the drawer's navigate({name:'Documents'}) hit no drawer
// route and silently no-opped.
function DocumentsStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Documents" component={DocumentsScreen} />
      <Stack.Screen name="AddDocument" component={AddDocumentScreen} />
    </Stack.Navigator>
  );
}

function RisksStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Risks" component={RisksScreen} />
      <Stack.Screen name="AddRisk" component={AddRiskScreen} />
    </Stack.Navigator>
  );
}

function EmployeesStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Employees" component={EmployeesScreen} />
      <Stack.Screen name="EmployeeDetail" component={EmployeeDetailScreen} />
      <Stack.Screen name="AddEmployee" component={AddEmployeeScreen} />
      <Stack.Screen name="ImportEmployees" component={ImportEmployeesScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
    </Stack.Navigator>
  );
}

function ExportStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Export" component={ExportScreen} />
    </Stack.Navigator>
  );
}

// AdminStack removed 2026-05-13 alongside AdminScreen.

function HelpStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Help" component={HelpScreen} />
    </Stack.Navigator>
  );
}

function KPIsStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="KPIs" component={KPIsScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
    </Stack.Navigator>
  );
}

function SchedulesStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Schedules" component={SchedulesScreen} />
    </Stack.Navigator>
  );
}

function NotificationsStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Notifications" component={NotificationsScreen} />
    </Stack.Navigator>
  );
}

function ProfileStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Profile" component={ProfileScreen} />
    </Stack.Navigator>
  );
}

function SettingsStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="Settings" component={SettingsScreen} />
    </Stack.Navigator>
  );
}

function EvaluationStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="EvaluationDashboard" component={EvaluationDashboard} />
      <Stack.Screen name="EvaluationDetail" component={EvaluationDetail} />
      <Stack.Screen name="CriteriaScoringForm" component={CriteriaScoringForm} />
      <Stack.Screen name="EvaluationReadOnly" component={EvaluationReadOnly} />
      <Stack.Screen name="AnnualEvalTable" component={AnnualEvalTable} />
      <Stack.Screen name="MonthlyEvalTable" component={MonthlyEvalTable} />
      <Stack.Screen name="EvaluationManagement" component={EvaluationManagement} />
      <Stack.Screen name="FrameworkBuilder" component={FrameworkBuilder} />
      <Stack.Screen name="GoalDetail" component={GoalDetailScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="ProcessDetail" component={ProcessDetailScreen} />
      <Stack.Screen name="SubProcessDetail" component={SubProcessDetailScreen} />
      <Stack.Screen name="AddSubProcess" component={AddSubProcessScreen} />
      <Stack.Screen name="AddOperation" component={AddOperationScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
    </Stack.Navigator>
  );
}

function SearchStack() {
  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="SearchResults" component={SearchResultsScreen} />
      {/* Detail and form screens for deep navigation from search results */}
      <Stack.Screen name="GoalDetail" component={GoalDetailScreen} />
      <Stack.Screen name="AddGoal" component={AddGoalScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="AddInitiative" component={AddInitiativeScreen} />
      <Stack.Screen name="ActivityDetail" component={ActivityDetailScreen} />
      <Stack.Screen name="AddActivity" component={AddActivityScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      <Stack.Screen name="ProcessDetail" component={ProcessDetailScreen} />
      <Stack.Screen name="AddProcess" component={AddProcessScreen} />
      <Stack.Screen name="SubProcessDetail" component={SubProcessDetailScreen} />
      <Stack.Screen name="AddSubProcess" component={AddSubProcessScreen} />
      <Stack.Screen name="AddOperation" component={AddOperationScreen} />
      <Stack.Screen name="ServiceDetail" component={ServiceDetailScreen} />
      <Stack.Screen name="AddService" component={AddServiceScreen} />
      <Stack.Screen name="UnitGoalDetail" component={UnitGoalDetailScreen} />
      <Stack.Screen name="AddUnitGoal" component={AddUnitGoalScreen} />
    </Stack.Navigator>
  );
}

// Strategy Organizations Stack (الجهات page for Strategy Team / DG / superuser)
function StrategyOrganizationsStack() {
  const { user } = useAuth();
  const navigation = useNavigation();
  // Section-scoped users (EMPLOYEE / MANAGER / DIRECTOR who aren't on the
  // strategy team) shouldn't be on this stack. The drawer hides it for them,
  // but a direct URL hit still routes them here — this guard bounces them.
  const isSectionScoped = !!user && (isEmployee(user) || (isOrgExecutive(user) && !canCreateStrategicGoals(user)));

  useEffect(() => {
    if (isSectionScoped) {
      navigation.dispatch(CommonActions.reset({ index: 0, routes: [{ name: 'DashboardStack' as any }] }));
    }
  }, [isSectionScoped, navigation]);

  if (isSectionScoped) {
    return null;
  }

  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="StrategyOrganizations" component={StrategyOrganizationsScreen} />
      <Stack.Screen name="OrganizationDetail" component={OrganizationDetailScreen} />
      <Stack.Screen name="Goals" component={GoalsScreen} />
      <Stack.Screen name="GoalDetail" component={GoalDetailScreen} />
      <Stack.Screen name="AddGoal" component={AddGoalScreen} />
      <Stack.Screen name="EmiratePlan" component={EmiratePlanScreen} />
      <Stack.Screen name="Partners" component={PartnersScreen} />
      <Stack.Screen name="ImportPartners" component={ImportPartnersScreen} />
      <Stack.Screen name="Documents" component={DocumentsScreen} />
      <Stack.Screen name="AddDocument" component={AddDocumentScreen} />
      <Stack.Screen name="CalibrationMeetings" component={CalibrationMeetingsScreen} />
      <Stack.Screen name="AuditLog" component={AuditLogScreen} />
      <Stack.Screen name="RecycleBin" component={RecycleBinScreen} />
      <Stack.Screen name="Initiatives" component={InitiativesScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="AddInitiative" component={AddInitiativeScreen} />
      <Stack.Screen name="ActivityDetail" component={ActivityDetailScreen} />
      <Stack.Screen name="AddActivity" component={AddActivityScreen} />
      <Stack.Screen name="ServiceDetail" component={ServiceDetailScreen} />
      <Stack.Screen name="AddService" component={AddServiceScreen} />
      <Stack.Screen name="ProcessDetail" component={ProcessDetailScreen} />
      <Stack.Screen name="AddProcess" component={AddProcessScreen} />
      <Stack.Screen name="SubProcessDetail" component={SubProcessDetailScreen} />
      <Stack.Screen name="AddSubProcess" component={AddSubProcessScreen} />
      <Stack.Screen name="AddOperation" component={AddOperationScreen} />
      <Stack.Screen name="UnitGoalDetail" component={UnitGoalDetailScreen} />
      <Stack.Screen name="AddUnitGoal" component={AddUnitGoalScreen} />
      <Stack.Screen name="KPIs" component={KPIsScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      <Stack.Screen name="Employees" component={EmployeesScreen} />
      <Stack.Screen name="EmployeeDetail" component={EmployeeDetailScreen} />
      <Stack.Screen name="AddEmployee" component={AddEmployeeScreen} />
      <Stack.Screen name="UnitGoals" component={UnitGoalsScreen} />
      <Stack.Screen name="Services" component={ServicesScreen} />
      <Stack.Screen name="Processes" component={ProcessesScreen} />
    </Stack.Navigator>
  );
}

// Strategy Team Stack (Strategy Team / DG / Director with approve_reject)
function StrategyStack() {
  const { user } = useAuth();
  const navigation = useNavigation();
  // Plain Section Employees don't belong on the strategy stack — bounce them
  // back to their own dashboard. Section Heads and Directors are allowed
  // because they hit screens like PendingApprovals here.
  const shouldBounceEmployee = !!user && isEmployee(user);

  useEffect(() => {
    if (shouldBounceEmployee) {
      navigation.dispatch(CommonActions.reset({ index: 0, routes: [{ name: 'DashboardStack' as any }] }));
    }
  }, [shouldBounceEmployee, navigation]);

  if (shouldBounceEmployee) {
    return null;
  }

  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen name="StrategyDashboard" component={StrategyDashboardScreen} />
      <Stack.Screen name="OrganizationsList" component={OrganizationsListScreen} />
      <Stack.Screen name="OrganizationDetail" component={OrganizationDetailScreen} />
      <Stack.Screen name="PendingApprovals" component={PendingApprovalsScreen} />
      <Stack.Screen name="PerformanceDeviation" component={PerformanceDeviationScreen} />
      <Stack.Screen name="Goals" component={GoalsScreen} />
      <Stack.Screen name="GoalDetail" component={GoalDetailScreen} />
      <Stack.Screen name="AddGoal" component={AddGoalScreen} />
      <Stack.Screen name="EmiratePlan" component={EmiratePlanScreen} />
      <Stack.Screen name="Partners" component={PartnersScreen} />
      <Stack.Screen name="ImportPartners" component={ImportPartnersScreen} />
      <Stack.Screen name="Documents" component={DocumentsScreen} />
      <Stack.Screen name="AddDocument" component={AddDocumentScreen} />
      <Stack.Screen name="CalibrationMeetings" component={CalibrationMeetingsScreen} />
      <Stack.Screen name="AuditLog" component={AuditLogScreen} />
      <Stack.Screen name="RecycleBin" component={RecycleBinScreen} />
      <Stack.Screen name="Initiatives" component={InitiativesScreen} />
      <Stack.Screen name="InitiativeDetail" component={InitiativeDetailScreen} />
      <Stack.Screen name="AddInitiative" component={AddInitiativeScreen} />
      <Stack.Screen name="ActivityDetail" component={ActivityDetailScreen} />
      <Stack.Screen name="AddActivity" component={AddActivityScreen} />
      <Stack.Screen name="ServiceDetail" component={ServiceDetailScreen} />
      <Stack.Screen name="AddService" component={AddServiceScreen} />
      <Stack.Screen name="ProcessDetail" component={ProcessDetailScreen} />
      <Stack.Screen name="AddProcess" component={AddProcessScreen} />
      <Stack.Screen name="SubProcessDetail" component={SubProcessDetailScreen} />
      <Stack.Screen name="AddSubProcess" component={AddSubProcessScreen} />
      <Stack.Screen name="AddOperation" component={AddOperationScreen} />
      <Stack.Screen name="UnitGoalDetail" component={UnitGoalDetailScreen} />
      <Stack.Screen name="AddUnitGoal" component={AddUnitGoalScreen} />
      <Stack.Screen name="KPIs" component={KPIsScreen} />
      <Stack.Screen name="KPIDetail" component={KPIDetailScreen} />
      <Stack.Screen name="AddKPIReading" component={AddKPIReadingScreen} />
      <Stack.Screen name="Schedules" component={SchedulesScreen} />
      <Stack.Screen name="SchedulesManagement" component={SchedulesManagementScreen} />
      <Stack.Screen name="Employees" component={EmployeesScreen} />
      <Stack.Screen name="EmployeeDetail" component={EmployeeDetailScreen} />
      <Stack.Screen name="AddEmployee" component={AddEmployeeScreen} />
      <Stack.Screen name="UnitGoals" component={UnitGoalsScreen} />
      <Stack.Screen name="Services" component={ServicesScreen} />
      <Stack.Screen name="Processes" component={ProcessesScreen} />
    </Stack.Navigator>
  );
}


export default function MainNavigator() {
  const { width } = useWindowDimensions();
  const { user, isLoading } = useAuth();
  const { isVisible: isSidebarVisible } = useSidebar();
  const { colors } = useTheme();
  const { t } = useLanguage();

  // Auto-redirect from DashboardStack to StrategyStack removed 2026-05-14
  // when the executive-style dashboard was hidden. DG now stays on the
  // unified DashboardStack like everyone else.

  // Determine initial route based on user role.
  //
  // 2026-05-14 update: every role now opens DashboardStack (the regular
  // "لوحة المعلومات"). The strategy-team executive dashboard was hidden
  // per user direction. Evaluators still land on their EvaluationStack
  // because they have a completely separate surface, and superuser
  // continues to drop straight onto the admin list.
  const getInitialRouteName = () => {
    if (user?.is_superuser) {
      return 'StrategyOrganizationsStack';
    }
    if (isEvaluator(user)) {
      return 'EvaluationStack';
    }
    return 'DashboardStack';
  };

  // Responsive breakpoints
  const isSmallScreen = width < 768;
  const isMediumScreen = width >= 768 && width < 1024;

  // Use permanent drawer on web for larger screens, front drawer for smaller screens
  const drawerType = Platform.OS === 'web'
    ? (isSmallScreen ? 'front' : 'permanent')
    : 'front';

  // Responsive drawer width - set to 0 when sidebar is hidden (web only)
  // On small screens: cap at 85% of screen width for very small phones, otherwise 260px
  const baseDrawerWidth = isSmallScreen ? Math.min(Math.round(width * 0.85), 260) : (isMediumScreen ? 220 : 240);
  const drawerWidth = Platform.OS === 'web' && !isSidebarVisible && !isSmallScreen ? 0 : baseDrawerWidth;

  // IMPORTANT: Wait for user data to load before rendering navigator
  // This ensures the correct initialRouteName is set based on actual user role.
  // We wait on position_type (the canonical field) instead of user_type so
  // accounts with no legacy role still proceed once their position lands.
  // Superuser is a special case — they have no position_type but is_superuser
  // is enough to gate routing.
  if (isLoading || !user || (!user.position_type && !user.is_superuser)) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.background }}>
        <Text style={{ fontSize: 16, color: colors.textMuted }}>{t('common.loading')}</Text>
      </View>
    );
  }

  // Use key to force re-mount navigator when user role changes.
  // Keyed on the routing dimensions (position + superuser + strategy-team
  // capability + evaluator status) so initialRouteName recomputes correctly
  // when an admin flips a role at runtime.
  const navigatorKey = [
    'navigator',
    user?.is_superuser ? 'su' : (user?.position_type || 'none'),
    canCreateStrategicGoals(user) ? 'strat' : '',
    isEvaluator(user) ? 'eval' : '',
    user?.id || 'unknown',
  ].filter(Boolean).join('-');

  return (
    <Drawer.Navigator
      key={navigatorKey}
      initialRouteName={getInitialRouteName()}
      drawerContent={(props) => <CustomDrawerContent {...props} />}
      screenOptions={{
        headerShown: false,
        drawerPosition: 'right', // RTL
        drawerStyle: {
          backgroundColor: colors.card,
          width: drawerWidth,
        },
        drawerType: drawerType,
        swipeEnabled: Platform.OS !== 'web' || isSmallScreen,
      }}
    >
      {/* Strategy Team Stack - also used by executive (VIEW ONLY) */}
      <Drawer.Screen name="StrategyStack" component={StrategyStack} />

      {/* Main Navigation Items - matching sidebar order */}
      <Drawer.Screen name="DashboardStack" component={DashboardStack} />
      <Drawer.Screen name="MyOrganizationStack" component={MyOrganizationStack} />
      <Drawer.Screen name="GoalsStack" component={GoalsStack} />
      <Drawer.Screen name="InitiativesStack" component={InitiativesStack} />
      <Drawer.Screen name="UnitGoalsStack" component={UnitGoalsStack} />
      <Drawer.Screen name="ProcessesStack" component={ProcessesStack} />
      <Drawer.Screen name="ServicesStack" component={ServicesStack} />
      <Drawer.Screen name="EvaluationStack" component={EvaluationStack} />
      <Drawer.Screen name="DocumentsStack" component={DocumentsStack} />
      <Drawer.Screen name="RisksStack" component={RisksStack} />
      <Drawer.Screen name="EmployeesStack" component={EmployeesStack} />
      <Drawer.Screen name="ExportStack" component={ExportStack} />
      <Drawer.Screen name="HelpStack" component={HelpStack} />

      {/* Hidden screens (not in sidebar but accessible) */}
      <Drawer.Screen name="OrganizationsStack" component={OrganizationsStack} />
      <Drawer.Screen name="SchedulesStack" component={SchedulesStack} />
      <Drawer.Screen name="NotificationsStack" component={NotificationsStack} />
      <Drawer.Screen name="ProfileStack" component={ProfileStack} />
      <Drawer.Screen name="SettingsStack" component={SettingsStack} />
      <Drawer.Screen name="SearchStack" component={SearchStack} />
      <Drawer.Screen name="StrategyOrganizationsStack" component={StrategyOrganizationsStack} />
    </Drawer.Navigator>
  );
}
